/* eslint-disable */
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IEmployeeListItem } from '../interfaces/IEmployeeListItem';
import { IEmployee } from '../interfaces/IEmployee';
import { INotification } from '../interfaces/INotification';
import { EMPLOYEE_ACTIVE_FILTER } from '../enums/EmployeeActiveFilter.enum';
import { IPersistentFilter } from '../interfaces/employee/IPersistentFilter';
import { NotificationService } from './notification.service';
import { ApiResult } from '../models/api-result.model';
import { IWorkingHours } from '../interfaces/IWorkingHours';
import { IPersistentFilterUpdateBody } from '../interfaces/IPersistentFilterUpdateBody';
import { IEmployeeEmergencyContact } from '../interfaces/IEmployeeEmergencyContact';
import { IPaginationProps } from '../interfaces/IPaginationProps';
import { IPaginatedResponse } from '../interfaces/IPaginatedResponse';
import { PaginationOperations } from '../operations/pagination.operations';
import { IElmDraftPromotionRequest, IElmDraftPromotionResult } from '../interfaces/employee/IElmDraftPromotion';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);

  private employeesApi = environment.apiUrl + 'Employees/';
  private workingHoursApi = environment.apiUrl + 'WorkingHours/';

  private httpOptions = {
    headers: new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }),
  };

  public getAllPicker(): Observable<IEmployeeListItem[]> {
    // will return all employees is without any restrictions
    return this.http.get(this.employeesApi + 'getAllPicker', {
      params: { 'Content-Type': 'application/json' },
    }) as Observable<IEmployeeListItem[]>;
  }

  public getEmployee(id: string): Observable<IEmployee> {
    return this.http.get<IEmployee>(this.employeesApi + 'GetSingleDetailById/' + id);
  }

  public promoteElmDraft(request: IElmDraftPromotionRequest): Observable<IElmDraftPromotionResult> {
    return this.http.post<IElmDraftPromotionResult>(`${environment.apiUrl}ScheduledEvents/ElmDraftPromotion`, request);
  }

  public getWorkingHours(id: string): Observable<IWorkingHours[]> {
    return this.http.get<IWorkingHours[]>(this.workingHoursApi + 'GetAllByUserAsync/' + id);
  }

  public updateWorkingHours(workingHours: IWorkingHours[]): Observable<ApiResult> {
    return this.http
      .post<ApiResult>(this.workingHoursApi + 'UpdateBatch', workingHours, this.httpOptions)
      .pipe(catchError(this.handleError<ApiResult>('updateWorkingHours')));
  }

  public getEmployees(employeePaginationProps: IPaginationProps): Observable<IPaginatedResponse<IEmployeeListItem>> {
    return this.http
      .get<IPaginatedResponse<IEmployeeListItem>>(this.employeesApi + 'GetFiltered', {
        params: PaginationOperations.getEmployeePaginatedResponseQueryParameters(employeePaginationProps),
      })
      .pipe(catchError(this.handleError<IPaginatedResponse<IEmployeeListItem>>('getEmployees')));
  }

  public getEmergencyContacts(id: string): Observable<IEmployeeEmergencyContact[]> {
    return this.http
      .get<IEmployeeEmergencyContact[]>(`${this.employeesApi}getSingleDetailById/${id}/emergencyConctacts`)
      .pipe(catchError(this.handleError<IEmployeeEmergencyContact[]>('getEmergencyContacts')));
  }

  public getEmployeeByGID(gid: string, isForCreate?: boolean, propagateError = false): Observable<IEmployee> {
    const params: { [key: string]: string } = {};
    if (isForCreate) {
      params['isForCreate'] = String(isForCreate);
    }

    const request = this.http.get<IEmployee>(this.employeesApi + 'getSingleDetailByGid/' + gid, { params });
    return propagateError ? request : request.pipe(catchError(this.handleError<IEmployee>(`getSingleEmployee id=${gid}`)));
  }

  public checkEmployeeStatus(gid: string, propagateError = false): Observable<ApiResult> {
    const request = this.http.get<ApiResult>(this.employeesApi + 'checkEmployeeStatus/' + gid);
    return propagateError ? request : request.pipe(catchError(this.handleError<ApiResult>('checkEmployeeStatus')));
  }

  public getPersistentFilter(gid: string): Observable<IPersistentFilter> {
    return this.http
      .get<any>(this.employeesApi + 'GetPersistentFilterByGid/' + gid)
      .pipe(catchError(this.handleError<IEmployeeListItem>('getPersistentFilter')));
  }

  public updatePersistentFilter(persistentFilterBody: IPersistentFilterUpdateBody): Observable<void> {
    return this.http
      .post<void>(this.employeesApi + 'UpdatePersistentFilterSingleValue/', persistentFilterBody)
      .pipe(catchError(this.handleError<void>('updatePersistentFilter')));
  }

  public updateEmployee(employee: IEmployee): Observable<ApiResult> {
    return this.http
      .post<ApiResult>(this.employeesApi + 'Update', employee, this.httpOptions)
      .pipe(catchError(this.handleError<ApiResult>('updateEmployee')));
  }

  public addEmployee(employee: IEmployee): Observable<ApiResult> {
    return this.http
      .post<ApiResult>(this.employeesApi + 'Create', employee, this.httpOptions)
      .pipe(catchError(this.handleError<ApiResult>('createEmployee')));
  }

  public addEmergencyContact(id: string, emContact: IEmployeeEmergencyContact): Observable<ApiResult | HttpErrorResponse> {
    return this.http.post<ApiResult>(`${this.employeesApi}getSingleDetailById/${id}/emergencyConctacts`, emContact, this.httpOptions);
  }

  public updateEmergencyContact(id: string, emContact: IEmployeeEmergencyContact): Observable<ApiResult | HttpErrorResponse> {
    return this.http.put<ApiResult>(
      `${this.employeesApi}getSingleDetailById/${id}/emergencyConctacts/${emContact.id}`,
      emContact,
      this.httpOptions,
    );
  }

  public deleteEmergencyContact(id: string, emContactId: string): Observable<ApiResult | HttpErrorResponse> {
    return this.http.delete<ApiResult>(`${this.employeesApi}getSingleDetailById/${id}/emergencyConctacts/${emContactId}`, this.httpOptions);
  }

  public getEmployeeEmailsByCountryId(countryId: number): Observable<string[]> {
    return this.http
      .get<string[]>(`${this.employeesApi}GetEmployeeEmailsByCountryId/${countryId}`, this.httpOptions)
      .pipe(catchError(this.handleError<string[]>('getEmployeeEmailsByCountryId')));
  }

  private handleError<T>(operation?: string): (error: any) => Observable<T> {
    return (error: any): any => {
      const notification: INotification = {
        type: 'Error',
        title: 'employees.employees',
        desc: operation,
      };
      this.notificationService.showMessage(notification);

      console.error(error);
      return of(error as Observable<any>);
    };
  }
}
